import { useEffect, useState, type FormEvent } from 'react';
import { suggestDevice } from '../../services/deviceSuggestions';

interface SuggestDeviceModalProps {
  open: boolean;
  initialName?: string;
  onClose: () => void;
}

export function SuggestDeviceModal({ open, initialName = '', onClose }: SuggestDeviceModalProps) {
  const [name, setName] = useState(initialName);
  const [brand, setBrand] = useState('');
  const [note, setNote] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'done' | 'error'>('idle');

  useEffect(() => {
    if (open) {
      setName(initialName);
      setBrand('');
      setNote('');
      setStatus('idle');
    }
  }, [open, initialName]);

  if (!open) return null;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setStatus('submitting');
    try {
      await suggestDevice({
        name: name.trim(),
        brand: brand.trim() || undefined,
        note: note.trim() || undefined,
      });
      setStatus('done');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Add a product to our database"
    >
      <div className="panel w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        {status === 'done' ? (
          <>
            <h3 className="text-lg font-semibold mb-2">Thanks — we&apos;ve got it</h3>
            <p className="text-sm text-ink-500 mb-4">
              We&apos;ll review &quot;{name}&quot; and add it to the catalog.
            </p>
            <button type="button" onClick={onClose} className="btn-panel">
              Close
            </button>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <h3 className="text-lg font-semibold">Add a product to our database</h3>
            <label className="flex flex-col gap-1 text-sm">
              Device name
              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input-panel"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Brand (optional)
              <input value={brand} onChange={(e) => setBrand(e.target.value)} className="input-panel" />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Anything else we should know? (optional)
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                className="input-panel"
              />
            </label>
            {status === 'error' && (
              <p className="text-sm text-accent-red">Couldn&apos;t submit — please try again.</p>
            )}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={onClose} className="btn-ghost">
                Cancel
              </button>
              <button type="submit" disabled={status === 'submitting'} className="btn-panel">
                {status === 'submitting' ? 'Sending…' : 'Submit'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
